import React, { useState } from 'react';
import { useFormik } from 'formik';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Loading from 'components/system/Loading';
import Input from 'components/system/Input';
import { addBalance } from 'api/services/payments';
import usePaymentMethod from 'store/paymentStore';
import { addBalanceSchema } from 'schemas/billing.schema';
import withNotification from 'components/hoc/withNotification';
import colors from 'styles/colors';
import Hint from 'components/system/Hint';

const styles = {
  amountChip: {
    backgroundColor: colors.primary.shade_16p,
    color: colors.text.primary,
    padding: '5px',
    marginRight: '10px',
    '&:hover': {
      backgroundColor: colors.primary.shade_30p,
    },
  },
  amountInput: {
    marginBottom: '16px',
    marginRight: '10px',
    flex: '1 1 0px',
  },
  label: (withLabel) => (withLabel ? {} : { textAlign: 'start' }),
  submitButton: {
    marginTop: 3,
    marginBottom: 2,
    flex: '1 1 0px',
  },
};

const suggestions = [10, 20, 100];

const initialValues = {
  amount: '',
};

const AddBalanceForm = ({
  contentLoading, hint, onBalanceUpdate, showErrorNotification, withLabel = true,
}) => {
  const [loading, setLoading] = useState(false);

  const card = usePaymentMethod((state) => state.paymentMethod.card);

  const formik = useFormik({
    initialValues,
    validationSchema: addBalanceSchema,
    onSubmit: async ({ amount }) => {
      setLoading(true);
      const { success } = await addBalance(amount, card);
      if (success) {
        setLoading(false);
        onBalanceUpdate();
      } else {
        setLoading(false);
        showErrorNotification('Payment failed. Please check your payment method.');
      }
    },
  });

  const handleChipClick = (amount) => {
    formik.setFieldValue('amount', amount, true);
  };

  return (
    <Box component="form" sx={{ width: '420px' }}>
      {contentLoading
        ? <Loading />
        : (
          <>
            {hint && <Hint>{hint}</Hint>}
            {withLabel ? (
              <Typography
                variant="h5"
                sx={{
                  color: colors.text.primary,
                  marginBottom: '25px',
                  textAlign: 'center',
                }}
              >
                Add Balance
              </Typography>
            ) : null}
            <Box sx={{ display: 'flex', flexDirection: withLabel ? 'column' : 'column-reverse' }}>
              <Box sx={{
                marginBottom: '10px',
                display: 'flex',
                justifyContent: withLabel ? 'center' : 'flex-start',
                alignItems: 'center',
              }}
              >
                {suggestions.map((amount) => (
                  <Chip
                    key={amount}
                    sx={styles.amountChip}
                    label={`$${amount}`}
                    variant="contained"
                    onClick={() => handleChipClick(amount)}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Input
                  sx={styles.amountInput}
                  labelSx={styles.label(withLabel)}
                  autoComplete="off"
                  label="Balance"
                  name="amount"
                  value={formik.values.amount}
                  error={formik.touched.amount && formik.errors.amount}
                  onChange={formik.handleChange}
                  startAdornment={(
                    <span
                      style={{
                        display: 'inline-block',
                        paddingRight: '3px',
                        color: colors.text.secondary,
                      }}
                    >
                      $
                    </span>
                  )}
                />
                <LoadingButton
                  fullWidth
                  onClick={formik.handleSubmit}
                  variant="contained"
                  type="primary"
                  loading={formik.isSubmitting}
                  disabled={formik.isSubmitting}
                  loadingPosition="end"
                  sx={styles.submitButton}
                >
                  Add
                </LoadingButton>
              </Box>
            </Box>
          </>
        )}
    </Box>
  );
};

export default withNotification(AddBalanceForm);
